import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Box, CircularProgress, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar, DateField } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import useFetch from '../../services/useFetch.js';
import GetData from '../../services/GetData.js';
import { states } from './UsStates.js';
import { FirstUpper } from '../../utils/FirstUpper.js';
import { tokens } from '../../theme';

const apiUrl = 'api/vendors';
const emptyHeader = {
  vendor: '',
  vendorId: null,
  street: '',
  city: '',
  zip: '',
  state: '',
  invoiceNum: '',
  date: '',
};

export default function ManualEntryHeader({ updateHeader, invoiceOrigin, invOriginVendor, uploadEdit, addressOptional }) {
  const [startAnchorElem, setStartAnchorElem] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const openStartCal = Boolean(startAnchorElem);
  const dateOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
  const stateArr = [...states].sort();
  const { data, load } = useFetch(apiUrl, false);
  const [rowData, setRowData] = useState([{ ...emptyHeader }]);

  const push = (next) => {
    setRowData(next);
    updateHeader?.(next);
  };

  useEffect(() => {
    if (invoiceOrigin && invOriginVendor && data) fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const fetchInvoice = async () => {
    const { getData } = await GetData(
      'api/invoicedetail',
      { invnum: invoiceOrigin, vendorId: invOriginVendor },
      null, null, null,
    );
    if (!getData) return;

    const reformDate = getData.date
      ? new Date(getData.date).toLocaleDateString('en-US', dateOptions)
      : '';

    // Prefer address from vendor master; fall back to invoice fields if present
    const vendorMatch = Array.isArray(data)
      ? data.find((v) => Number(v.vendorId) === Number(getData.vendorId || invOriginVendor))
      : null;

    const next = [{
      ...rowData[0],
      _id: getData._id || invoiceOrigin,
      vendor: getData.vendor || vendorMatch?.vendor || '',
      vendorId: getData.vendorId ?? invOriginVendor,
      street: getData.street || vendorMatch?.street || '',
      city: getData.city || vendorMatch?.city || '',
      state: getData.state || vendorMatch?.state || '',
      zip: getData.zip || vendorMatch?.zip || '',
    }];

    if (uploadEdit) {
      next[0].date = reformDate;
      next[0].invoiceNum = getData.invoiceNum || '';
      setStartDate(reformDate || null);
    }
    push(next);
  };

  const handleVenChange = (e, val, reason) => {
    if (reason === 'clear') {
      push([{ ...emptyHeader }]);
      return;
    }
    const upperVal = val?.length > 0 ? FirstUpper(val) : val;
    const next = [{ ...rowData[0], vendor: upperVal }];
    const nmIndx = Array.isArray(data) ? data.findIndex((item) => item.vendor === val) : -1;
    if (nmIndx >= 0) {
      next[0].street = data[nmIndx].street || '';
      next[0].vendorId = data[nmIndx].vendorId;
      next[0].city = data[nmIndx].city || '';
      next[0].state = data[nmIndx].state || '';
      next[0].zip = data[nmIndx].zip || '';
    }
    push(next);
  };

  const setField = (key, value) => {
    const next = [{ ...rowData[0], [key]: value }];
    push(next);
  };

  const handleDateField = (value) => {
    if (!value) {
      setStartDate(null);
      setField('date', '');
      return;
    }
    const date = value?.toDate ? value.toDate() : new Date(value);
    const reformDate = date.toLocaleDateString('en-US', dateOptions);
    setStartDate(reformDate);
    setField('date', reformDate);
  };

  const handleStartDate = ({ target }) => {
    if (!target?.dataset?.timestamp) return;
    const date = new Date(parseInt(target.dataset.timestamp, 10));
    const reformDate = date.toLocaleDateString('en-US', dateOptions);
    setStartDate(reformDate);
    setStartAnchorElem(null);
    setField('date', reformDate);
  };

  if (load) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const vendorOptions = Array.isArray(data) ? data.map((v) => v.vendor) : [];
  const header = rowData[0];

  return (
    <>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Autocomplete
            freeSolo
            fullWidth
            options={vendorOptions}
            value={header.vendor || ''}
            onInputChange={(e, val, reason) => handleVenChange(e, val, reason)}
            renderInput={(params) => (
              <TextField {...params} label="Vendor" required placeholder="Vendor name" />
            )}
          />
          <TextField
            label="Invoice #"
            required
            fullWidth
            value={header.invoiceNum || ''}
            onChange={(e) => setField('invoiceNum', e.target.value)}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateField
              label="Invoice date"
              required
              fullWidth
              value={startDate ? dayjs(startDate) : null}
              onChange={handleDateField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => setStartAnchorElem(e.currentTarget)}
                      aria-label="Open calendar"
                    >
                      <CalendarMonthIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </LocalizationProvider>
        </Stack>

        <TextField
          label="Street"
          required={!addressOptional}
          fullWidth
          value={header.street || ''}
          onChange={(e) => setField('street', e.target.value)}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="City"
            required={!addressOptional}
            fullWidth
            value={header.city || ''}
            onChange={(e) => setField('city', e.target.value)}
          />
          <Autocomplete
            freeSolo
            fullWidth
            options={stateArr}
            value={header.state || ''}
            onInputChange={(e, val) => setField('state', val)}
            renderInput={(params) => (
              <TextField {...params} label="State" required={!addressOptional} />
            )}
          />
          <TextField
            label="Zip"
            required={!addressOptional}
            fullWidth
            value={header.zip || ''}
            onChange={(e) => setField('zip', e.target.value)}
          />
        </Stack>
      </Stack>

      <Menu
        open={openStartCal}
        onClose={() => setStartAnchorElem(null)}
        anchorEl={startAnchorElem}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${tokens.border}`,
            overflow: 'hidden',
          },
        }}
      >
        <MenuItem disableRipple sx={{ p: 0, '&:hover': { backgroundColor: 'transparent' } }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={startDate ? dayjs(startDate) : dayjs()}
              onClick={handleStartDate}
            />
          </LocalizationProvider>
        </MenuItem>
      </Menu>
    </>
  );
}
